import { Translate } from 'next-translate'
import { BasketPresenter, SchedulablePresenter } from 'shared/presenters'
import { definedNotNull } from 'shared/presenters/presenter'
import { OptionalExtra } from 'shared/helpers/formatOptionalExtraData/formatOptionalExtras'
import getHoursFromHM from 'helpers/getHoursFromHM'
import getPeopleGoingText from 'helpers/getPeopleGoingText'
import { RentalRequirement } from 'helpers/formatRentalRequirements'
import { Timeslot } from 'shared/helpers/formatRentalBookabilities/TimeSlot'

/**
 * The chargeable review section can load different groups of chargeable, i.e. (Vouchers, Schedulable, Rentals etc.)
 *
 * This function determines what should be rendered and formats the data to load the review section on the basket or
 * while configuring a chargeable.
 * */

export interface ChargeableReviewData {
  pageTitle: string
  chargeableTitle: string
  summary: Summary
  lineItemSummary: LineItemSummary
  total: number
  isGift: boolean
  location?: unknown // TODO - #3360 - Waiting for the map component to introduce the logic to render the location
  basket?: BasketPresenter
}

export interface DetermineChargeableReviewDataForSchedulables {
  chargeable: SchedulablePresenter
  adults: number
  children: number
  /**
   * Activities use TimeSlotPresenter but rentals have their own formatted data.
   * Passing specific values for start or finish instead of timeSlot removes the need to handle
   * the different data generated by activities and rentals.
   */
  timeSlot?: Pick<Timeslot, 'start' | 'finish'> | undefined
  rentalRequirements?: RentalRequirement[]
  optionalExtras?: OptionalExtra[]
  exclusive?: boolean
  total: number
  isGift?: boolean
  basket?: BasketPresenter
  t: Translate
}

/**
 * Format the schedulables data (activities and rentals) for the ChargeableReview component.
 */
export const determineChargeableReviewDataForSchedulables = ({
  chargeable,
  adults,
  children,
  timeSlot,
  total,
  isGift,
  basket,
  rentalRequirements,
  optionalExtras,
  exclusive,
  t
}: DetermineChargeableReviewDataForSchedulables): ChargeableReviewData => {
  const summary = generateSummary({
    adults,
    children,
    optionalExtras,
    rentalRequirements,
    timeSlot: timeSlot && timeSlot,
    exclusive,
    arriveBeforeMins:
      chargeable.arrive_before_mins && chargeable.arrive_before_mins,
    isMultiDay: chargeable.isMultiDay(),
    continuous: chargeable.continuous,
    t
  })

  const lineItemSummary = generateLineItemSummary({
    adults,
    children,
    optionalExtras,
    rentalRequirements,
    timeSlot: timeSlot && timeSlot,
    exclusive,
    arriveBeforeMins:
      chargeable.arrive_before_mins && chargeable.arrive_before_mins,
    isMultiDay: chargeable.isMultiDay(),
    continuous: chargeable.continuous,
    t
  })

  return {
    pageTitle: `${t('chargeable:my-booking')} - ${chargeable.name()} | eola`,
    chargeableTitle: chargeable.name(),
    summary,
    lineItemSummary,
    total,
    isGift: !!isGift,
    location: undefined, // TODO - See interface ChargeableReviewData for more info
    basket
  }
}

/**
 * The date format used for chargeable data
 */
export const DATE_FORMAT = 'dddd Do MMM'

/**
 * Using (string | string[])[] to allow each item to have subitems.
 */
type Summary = (string | string[])[]
interface LineItemSummary {
  participants: string
  when: string
  addons: string
  rentals: string
  exclusive: boolean
}
interface GenerateSummaryProps
  extends Pick<
    DetermineChargeableReviewDataForSchedulables,
    'adults' | 'children' | 't' | 'timeSlot'
  > {
  optionalExtras?: OptionalExtra[]
  rentalRequirements?: RentalRequirement[]
  exclusive?: boolean
  arriveBeforeMins: number | undefined
  isMultiDay?: boolean
  continuous?: boolean
}

const generateSummary = ({
  adults,
  children,
  optionalExtras,
  rentalRequirements,
  timeSlot,
  arriveBeforeMins,
  exclusive,
  isMultiDay = false,
  continuous,
  t
}: GenerateSummaryProps): Summary => {
  const summary: ChargeableReviewData['summary'] = []

  if (adults || children) {
    const attendants: string[] = []
    adults && attendants.push(getPeopleGoingText({ adults }, t, true))
    children && attendants.push(getPeopleGoingText({ children }, t, true))
    summary.push(attendants)
  }

  if (optionalExtras) {
    const optionalExtra: string[] = generateOptionalExtras(optionalExtras, t)

    optionalExtra.length && summary.push(optionalExtra)
  }

  if (rentalRequirements) {
    const rentalResources: string[] = generateRentalResources(
      rentalRequirements,
      t
    )

    rentalResources.length && summary.push(rentalResources)
  }

  const timeDetails: string[] | undefined = []

  if (!timeSlot) {
    timeDetails.push('')
  }

  if (timeSlot?.start && !arriveBeforeMins) {
    const { dateText, timeText } = generateTimeSummary(
      t,
      timeSlot,
      isMultiDay,
      continuous
    )

    timeDetails.push(definedNotNull(dateText))
    timeDetails.push(timeText)
    summary.push(timeDetails)
  }

  if (timeSlot?.start && arriveBeforeMins) {
    const { dateText, timeText } = generateTimeSummary(
      t,
      timeSlot,
      isMultiDay,
      continuous
    )

    timeDetails.push(definedNotNull(dateText))
    timeDetails.push(timeText)

    const arriveAt = timeSlot?.start
      .subtract(arriveBeforeMins, 'm')
      .format('kk:mm')

    timeDetails.push(
      t('common:arrive-at', {
        count: getHoursFromHM(arriveAt),
        hours: arriveAt
      })
    )
    summary.push(timeDetails)
  }

  if (exclusive) summary.push(t('chargeable:exclusive-booking'))
  return summary
}

const generateLineItemSummary = ({
  adults,
  children,
  rentalRequirements,
  optionalExtras,
  timeSlot,
  exclusive,
  isMultiDay = false,
  continuous,
  t
}: GenerateSummaryProps): LineItemSummary => {
  const lineItemSummary = {
    when: '',
    participants: '',
    addons: '',
    rentals: '',
    exclusive: !!exclusive
  }
  if (timeSlot?.start) {
    const { dateText, timeText } = generateTimeSummary(
      t,
      timeSlot,
      isMultiDay,
      continuous
    )

    lineItemSummary.when = `${definedNotNull(dateText)}, ${timeText}`
  }

  if (adults || children) {
    let participants = ''
    if (adults) participants += getPeopleGoingText({ adults }, t, true)
    if (children) {
      if (adults) participants += ', '
      participants += getPeopleGoingText({ children }, t, true)
    }
    lineItemSummary.participants = participants
  }

  if (rentalRequirements) {
    const rentalResources: string[] = generateRentalResources(
      rentalRequirements,
      t
    )

    if (rentalResources.length)
      lineItemSummary.rentals = rentalResources.join(', ')
  }

  if (optionalExtras) {
    const optionalExtra: string[] = generateOptionalExtras(optionalExtras, t)

    if (optionalExtra.length) lineItemSummary.addons = optionalExtra.join(', ')
  }

  return lineItemSummary
}

const generateTimeSummary = (
  t: Translate,
  timeSlot: Pick<Timeslot, 'start' | 'finish'> | undefined,
  isMultiDay: boolean,
  continuous?: boolean
) => {
  const TIME_FORMAT = 'kk:mm'
  const startDateText = timeSlot?.start?.format(DATE_FORMAT)
  const startTimeText = `${timeSlot?.start?.format(
    TIME_FORMAT
  )} - ${timeSlot?.finish?.format(TIME_FORMAT)}`

  const dateText = !isMultiDay
    ? startDateText
    : `${startDateText} - ${timeSlot?.finish?.format(DATE_FORMAT)}`
  const timeText = !isMultiDay
    ? startTimeText
    : `${startTimeText} ${
        continuous
          ? t('chargeable:continuous-parentheses')
          : t('chargeable:everyday-parentheses')
      }`

  return { dateText, timeText }
}

const generateRentalResources = (
  rentalRequirements: RentalRequirement[],
  t: Translate
) => {
  return rentalRequirements
    .filter(item => item.count > 0)
    .map(item =>
      t('chargeable:item-quantity', { count: item.count, item: item.name })
    )
}

const generateOptionalExtras = (
  optionalExtras: OptionalExtra[],
  t: Translate
) => {
  return optionalExtras
    .filter(item => item.quantity > 0)
    .map(item =>
      t('chargeable:item-quantity', {
        count: item.quantity,
        item: item.name
      })
    )
}
